import { Link } from 'react-router';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useSEO, COMPETITIVE_KEYWORD_CLUSTERS, mergeKeywordSets } from '../lib/seo';
import { StockImage, WarmMarketingBackdrop } from '../components/marketing/MarketingVisuals';
import { ArrowRight, BookOpen, MessageSquareHeart, ShieldCheck } from 'lucide-react';

const guides = [
  {
    title: '7-Day Conflict De-escalation Plan',
    description: 'A practical daily sequence to reduce reactivity and improve repair after a hard week.',
    to: '/blog/how-to-reduce-relationship-tension',
  },
  {
    title: 'Attachment Style Reset',
    description: 'How to spot anxious or avoidant loops early and return to secure communication patterns.',
    to: '/blog/attachment-theory-explained',
  },
  {
    title: 'Conversation Scripts for Difficult Moments',
    description: 'Simple language templates you can use when emotions are high and clarity is low.',
    to: '/resources',
  },
];

export default function Guides() {
  useSEO({
    title: 'Relationship Guides | TENSION',
    description:
      'Actionable relationship guides for communication, conflict repair, and emotional safety. Practical frameworks you can use immediately.',
    keywords: mergeKeywordSets(
      COMPETITIVE_KEYWORD_CLUSTERS.conflict as unknown as string[],
      COMPETITIVE_KEYWORD_CLUSTERS.communication as unknown as string[],
      ['relationship guides', 'conflict repair', 'attachment style help'],
    ),
    canonical: '/guides',
    ogType: 'website',
    twitterCard: 'summary_large_image',
  });

  return (
    <WarmMarketingBackdrop>
      <div className="mx-auto max-w-5xl space-y-10 px-4 py-10 md:py-14">
        <header className="marketing-panel grid gap-8 md:grid-cols-[1.1fr_0.9fr] md:items-center">
          <div className="space-y-4 p-6 md:p-10">
            <p className="inline-flex rounded-full bg-violet-100 px-3 py-1 text-xs font-bold uppercase tracking-wider text-violet-800 ring-1 ring-violet-200/80">
              TENSION guides
            </p>
            <h1 className="text-3xl font-black tracking-tight text-neutral-900 md:text-4xl lg:text-[2.5rem] lg:leading-tight">
              Relationship counseling app guides you can apply today
            </h1>
            <p className="text-base leading-relaxed text-neutral-600 md:text-lg">
              Built for real life: low effort, practical language, and measurable progress—not theory overload.
            </p>
          </div>
          <div className="relative min-h-[200px]">
            <StockImage
              stockKey="coupleOutdoor"
              width={900}
              height={700}
              priority
              alt="Couple spending relaxed time together outdoors"
              className="h-full min-h-[200px] w-full object-cover md:rounded-none md:rounded-r-3xl"
            />
            <div
              className="pointer-events-none absolute inset-0 bg-gradient-to-r from-white via-transparent to-transparent md:from-white/90"
              aria-hidden
            />
          </div>
        </header>

        <div className="grid gap-4 md:grid-cols-3">
          <Card className="border-violet-100/90 bg-white/90 p-6 shadow-md transition-shadow hover:shadow-lg">
            <BookOpen className="h-6 w-6 text-violet-600" />
            <h2 className="mt-3 font-semibold text-neutral-900">Clear structure</h2>
            <p className="mt-2 text-sm text-neutral-600">No theory overload. Each guide gives exact next steps.</p>
          </Card>
          <Card className="border-violet-100/90 bg-white/90 p-6 shadow-md transition-shadow hover:shadow-lg">
            <MessageSquareHeart className="h-6 w-6 text-violet-600" />
            <h2 className="mt-3 font-semibold text-neutral-900">Useable scripts</h2>
            <p className="mt-2 text-sm text-neutral-600">Copy-ready language for hard conversations.</p>
          </Card>
          <Card className="border-violet-100/90 bg-white/90 p-6 shadow-md transition-shadow hover:shadow-lg">
            <ShieldCheck className="h-6 w-6 text-violet-600" />
            <h2 className="mt-3 font-semibold text-neutral-900">Emotion-safe</h2>
            <p className="mt-2 text-sm text-neutral-600">Designed to lower escalation, not win arguments.</p>
          </Card>
        </div>

        <section className="space-y-4">
          {guides.map((guide) => (
            <Card
              key={guide.title}
              className="border-violet-100/90 bg-white/95 p-6 shadow-md transition-all hover:-translate-y-0.5 hover:shadow-xl md:p-8"
            >
              <h2 className="text-xl font-semibold text-neutral-900">{guide.title}</h2>
              <p className="mt-2 text-neutral-600">{guide.description}</p>
              <Link to={guide.to} className="mt-5 inline-flex">
                <Button className="bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white shadow-md hover:opacity-95">
                  Open guide
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </Card>
          ))}
        </section>

        <Card className="border-violet-200/80 bg-gradient-to-r from-violet-50/95 to-fuchsia-50/90 p-8 shadow-lg">
          <h2 className="text-xl font-semibold text-neutral-900">Need personalized help?</h2>
          <p className="mt-2 text-neutral-700">
            Use Relationship Intelligence and Conversation Micro-Coach for tailored guidance.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/features/relationship-intelligence">
              <Button className="bg-violet-700 hover:bg-violet-800">Explore Relationship Intelligence</Button>
            </Link>
            <Link to="/features/conversation-micro-coach">
              <Button variant="outline" className="border-violet-300 bg-white/90">
                Explore Micro-Coach
              </Button>
            </Link>
            <Link to="/alternatives/paired">
              <Button variant="outline" className="border-violet-300 bg-white/90">
                Paired alternatives
              </Button>
            </Link>
          </div>
        </Card>
      </div>
    </WarmMarketingBackdrop>
  );
}
